import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Copy, ChevronRight, Loader2, ExternalLink } from "lucide-react";
import { StepIndicator } from "./StepIndicator";

const BREVO = "#0B996E";

export function BrevoGuidedSetup({ integration, formData, setFormData, webhookInfo, handleUpdate, handleTest, testing, copyToClipboard }) {
  const [currentStep, setCurrentStep] = useState(1);
  const creds = formData.credentials || {};
  const hasApiKey = !!creds.api_key;

  const steps = [
    { step: 1, label: "Get your Brevo API Key" },
    { step: 2, label: "Add the POMEN webhook to Brevo" },
    { step: 3, label: "Trigger webhook from a workflow & Test" },
  ];

  const effectiveStep = !hasApiKey ? 1 : currentStep < 2 ? 2 : currentStep;

  return (
    <div className="space-y-4" data-testid="brevo-guided-setup">
      <div className="space-y-2">
        {steps.map(s => (
          <button key={s.step} onClick={() => setCurrentStep(s.step)} className="w-full text-left">
            <StepIndicator step={s.step} currentStep={effectiveStep < s.step ? effectiveStep : currentStep >= s.step ? currentStep : effectiveStep} label={s.label} />
          </button>
        ))}
      </div>

      {currentStep === 1 && (
        <div className="space-y-4 pt-2">
          <div className="p-4 rounded-xl" style={{ background: `${BREVO}0D`, border: `1px solid ${BREVO}26` }}>
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Step 1: Get your Brevo API Key</h4>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>1.</span>
                <span>Log in to <a href="https://app.brevo.com" target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: BREVO }}>Brevo</a> (formerly Sendinblue).</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>2.</span>
                <span>Top-right menu → <strong>SMTP & API</strong> → <strong>API Keys</strong> tab.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>3.</span>
                <span>Click <strong>Generate a new API key</strong>, name it <em>POMEN Lead Tracker</em>, copy the <strong>v3 key</strong> (it starts with <code className="bg-white px-1 rounded border">xkeysib-…</code>).</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>4.</span>
                <span>Paste it below and save.</span>
              </li>
            </ol>
            <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs underline mt-3" style={{ color: BREVO }}>Open Brevo API Keys page <ExternalLink className="w-3 h-3" /></a>
          </div>
          <div className="space-y-2">
            <Label className="font-medium">Brevo API Key</Label>
            <Input
              type="password"
              value={creds.api_key || ""}
              onChange={(e) => setFormData({ ...formData, credentials: { ...creds, api_key: e.target.value } })}
              placeholder="xkeysib-…"
              className="border-gray-300 rounded-lg font-mono text-sm"
              data-testid="brevo-api-key"
            />
            <p className="text-[11px] text-gray-500">Stored encrypted. We use it only to enrich contacts when a webhook fires.</p>
          </div>
          <Button onClick={() => { handleUpdate(); setCurrentStep(2); }} disabled={!creds.api_key} className="w-full text-white rounded-lg" style={{ background: BREVO }} data-testid="brevo-step1-next">
            Save & Continue <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {currentStep === 2 && (
        <div className="space-y-4 pt-2">
          <div className="p-4 rounded-xl" style={{ background: `${BREVO}0D`, border: `1px solid ${BREVO}26` }}>
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Step 2: Register the POMEN webhook in Brevo</h4>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>1.</span>
                <span>In Brevo, open <strong>Automation</strong> → <strong>Transactional</strong> → <strong>Settings → Webhook</strong> (or <a href="https://app.brevo.com/settings/webhook" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: BREVO }}>direct link</a>).</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>2.</span>
                <span>Click <strong>Add a new webhook</strong>.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>3.</span>
                <span>Paste the <strong>Callback URL</strong> below.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>4.</span>
                <span>Enable the events you care about. Recommended: <strong>Contact created</strong>, <strong>Contact updated</strong>, <strong>Form submission</strong>, and any <strong>Marketing/Transactional</strong> events you want to drive leads from.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>5.</span>
                <span>Add the header <code className="bg-white px-1 rounded border">x-pomen-token</code> with the <strong>Verify Token</strong> below (Brevo &gt; Custom Headers).</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>6.</span>
                <span>Save.</span>
              </li>
            </ol>
          </div>

          {webhookInfo && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div>
                <Label className="text-xs font-medium text-gray-500 mb-1 block">Callback URL</Label>
                <div className="flex gap-2">
                  <code className="flex-1 text-xs bg-white px-3 py-2.5 rounded-lg border border-gray-200 font-mono break-all select-all" data-testid="brevo-webhook-url">{webhookInfo.webhook_url}</code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.webhook_url)} className="shrink-0 h-9"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-500 mb-1 block">Verify Token (custom header: <code className="bg-white px-1 rounded border">x-pomen-token</code>)</Label>
                <div className="flex gap-2">
                  <code className="flex-1 text-xs bg-white px-3 py-2.5 rounded-lg border border-gray-200 font-mono select-all" data-testid="brevo-verify-token">{webhookInfo.verify_token}</code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.verify_token)} className="shrink-0 h-9"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentStep(1)} className="border-gray-300 rounded-lg">Back</Button>
            <Button onClick={() => setCurrentStep(3)} className="flex-1 text-white rounded-lg" style={{ background: BREVO }} data-testid="brevo-step2-next">
              I've Done This <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className="space-y-4 pt-2">
          <div className="p-4 rounded-xl" style={{ background: `${BREVO}0D`, border: `1px solid ${BREVO}26` }}>
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Step 3: Trigger the webhook from a Workflow & Test</h4>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>1.</span>
                <span>In Brevo, open <strong>Automation → Workflows</strong> and create / edit the journey that captures your leads (e.g. <em>"New contact from landing page"</em>).</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>2.</span>
                <span>Add a <strong>"Send a webhook"</strong> action. Paste the same <strong>Callback URL</strong> from Step 2.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>3.</span>
                <span>Map at minimum: <code className="bg-white px-1 rounded border">EMAIL</code>, <code className="bg-white px-1 rounded border">FIRSTNAME</code>, <code className="bg-white px-1 rounded border">LASTNAME</code>, <code className="bg-white px-1 rounded border">SMS</code>. Add a <strong>WORKSHOP_NAME</strong> attribute if you collect it on the form.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>4.</span>
                <span>Set the verify-token header on the workflow webhook the same way as Step 2.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>5.</span>
                <span>Click <strong>Test Connection</strong> below to verify your API key is valid.</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold shrink-0" style={{ color: BREVO }}>6.</span>
                <span>Submit a test contact in Brevo — within seconds it will appear in <strong>Leads</strong> with source = <em>Brevo</em>.</span>
              </li>
            </ol>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
            <h5 className="text-sm font-semibold text-gray-900">Supported event types</h5>
            <div className="flex flex-wrap gap-1.5">
              {["Contact created", "Contact updated", "Form submission", "List added", "Workflow webhook"].map(ch => (
                <span key={ch} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-600">{ch}</span>
              ))}
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
            <h5 className="text-sm font-semibold text-gray-900">Configuration Summary</h5>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-gray-500">API Key:</span> <span className={creds.api_key ? "text-emerald-600 font-medium" : "text-red-500"}>{creds.api_key ? "Configured" : "Missing"}</span></div>
              <div><span className="text-gray-500">Callback URL:</span> <span className={webhookInfo ? "text-emerald-600 font-medium" : "text-red-500"}>{webhookInfo ? "Ready" : "Not generated"}</span></div>
              <div><span className="text-gray-500">Status:</span> {integration.status === "connected" ? <span className="text-emerald-600 font-medium">Connected</span> : <span className="text-amber-600 font-medium">Pending test</span>}</div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentStep(2)} className="border-gray-300 rounded-lg">Back</Button>
            <Button variant="outline" onClick={handleTest} disabled={testing} className="flex-1" style={{ borderColor: BREVO, color: BREVO }} data-testid="brevo-test-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Test Connection
            </Button>
          </div>

          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-xs text-emerald-800 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span><strong>How it works:</strong> Whenever Brevo fires the webhook (form submit, contact added/updated, workflow action) we receive the payload, optionally enrich it via the API key, then create a POMEN lead with source = <em>Brevo</em>. Duplicates are detected by email/phone within the Brevo source.</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
